import React from 'react';
import styled from 'styled-components';

import BalanceButton from './BalanceButton';
import MenuList from './MenuList';
import formatCurrency from '../../../utils/formatCurrency';
import { ClickAnywhereElseToCloseCover } from '../AppMenu/components';
import { ClientUserBonus } from '../../../@types/ClientUserBonus';
import { ClientAppConfig } from '../../../appsConfig';
import GiftIcon from '../Rewards/GiftIcon';
import { useBonusContext } from '../../../contexts/BonusContext';
import BonusMenu from '../Rewards/BonusMenu';
import { useRewards } from '../Rewards/useRewards';

interface Props {
	activeApp: ClientAppConfig;
	hideBalance: boolean;
	isSmallScreen: boolean;
	setHideBalance: (hideBalance: boolean) => void;
	onUpdatebonuses: () => void;
	user: PortalUser;
	logout: () => void;
	clientUserBonuses: Record<number, Array<ClientUserBonus>>;
	sessionStartTime?: string;
}

const BalanceMenu = ({
	activeApp,
	hideBalance,
	isSmallScreen,
	onUpdatebonuses,
	clientUserBonuses,
	setHideBalance,
	sessionStartTime,
	logout,
	user,
}: Props) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [isRewardModalOpen, setIsRewardModalOpen] = React.useState(false);
	const toggleMenu = React.useCallback(
		() =>
			setIsOpen((preState) => {
				if (!preState) {
					setIsRewardModalOpen(false);
				}
				return !preState;
			}),
		[]
	);
	const toggleHideBalance = React.useCallback(() => setHideBalance(!hideBalance), [hideBalance, setHideBalance]);
	const toggleRewardModal = React.useCallback(
		() =>
			setIsRewardModalOpen((preState) => {
				if (!preState) {
					setIsOpen(false);
				}
				return !preState;
			}),
		[]
	);
	React.useEffect(() => {
		if (isOpen) onUpdatebonuses();
	}, [isOpen, onUpdatebonuses]);

	const [bonuses, linkCounters] = React.useMemo(() => {
		const activeClientBonuses = clientUserBonuses[activeApp.clientId];

		if (!activeClientBonuses) return [[], []];

		const bonuses = activeApp.bonusTypes.map((bonusType) => {
			const amount = activeClientBonuses.find((bonus) => bonus.type === bonusType.type)?.total ?? 0;

			return {
				...bonusType,
				amount,
			};
		});

		const activeAppCounters = activeApp.balanceLinks.reduce<Array<{ key: string; count: number }>>((acc, link) => {
			if (!link.counterKey) return acc;
			const linkCounter = activeClientBonuses.find((bonus) => bonus.type === link.counterKey);

			return [...acc, { key: link.counterKey, count: linkCounter?.total || 0 }];
		}, []);

		return [bonuses, activeAppCounters];
	}, [activeApp, clientUserBonuses]);
	const { draftstarsRewards } = useBonusContext();
	const { wageringRewardCounts } = useRewards();
	return (
		<div className="_flex _flex-row">
			<Wrapper>
				<IconWrapper id='my-rewards' isSmallScreen={isSmallScreen} isOpen={isRewardModalOpen} onClick={toggleRewardModal}>
					{wageringRewardCounts + draftstarsRewards.length > 0 && (
						<Badge>{wageringRewardCounts + draftstarsRewards.length}</Badge>
					)}

					<GiftIcon width={20} height={20} color="white" />
				</IconWrapper>
				{isRewardModalOpen && (
					<>
						<BonusMenu toggleMenu={toggleRewardModal} isSmallScreen={isSmallScreen} activeApp={activeApp} />
						<ClickAnywhereElseToCloseCover onClick={toggleRewardModal} $opaque />
					</>
				)}
			</Wrapper>
			<Wrapper>
				<BalanceButton toggleMenu={toggleMenu} isSmallScreen={isSmallScreen} isOpen={isOpen}>
					{hideBalance ? `Hi, ${user.first_name}` : formatCurrency(user.balance)}
				</BalanceButton>
				{isOpen && (
					<>
						<MenuList
							user={user}
							bonuses={bonuses}
							linkCounters={linkCounters}
							activeApp={activeApp}
							hideBalance={hideBalance}
							toggleHideBalance={toggleHideBalance}
							toggleMenu={toggleMenu}
							isSmallScreen={isSmallScreen}
							logout={logout}
							sessionStartTime={sessionStartTime}
						/>
						<ClickAnywhereElseToCloseCover onClick={toggleMenu} $opaque />
					</>
				)}
			</Wrapper>
		</div>
	);
};

export default BalanceMenu;
const Badge = styled.div({
	width: 17,
	height: 17,
	backgroundColor: 'red',
	borderRadius: '50%',
	position: 'absolute',
	right: -8.5,
	top: -8.5,
	borderWidth: 2,
	borderColor: 'black',
	color: 'white',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	fontSize: 10,
	fontWeight: '600',
	paddingBottom: 1,
	paddingLeft: 1,
});
const IconWrapper = styled.div<{ isSmallScreen: boolean; isOpen: boolean }>(({ isOpen, isSmallScreen }) => ({
	backgroundColor: 'white',
	width: '28px',
	height: '28px',
	justifyContent: 'center',
	alignItems: 'center',
	display: 'flex',
	borderRadius: 3,
	marginRight: 20,
	position: 'relative',
	zIndex: isOpen ? 61 : 'inherit',
	cursor: 'pointer',
}));
const Wrapper = styled.div`
	position: relative;
`;
